import { UIElement } from "./ui_element.js";
import { ImgID } from "../../image_loader/img_id.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { TransformOptions } from "./ui_element.js";
import { Vector2 } from "../../common/vector2.js";
import { DOM_SYNC_DECODE_MIN_BYTES, mountDomSyncImage } from "../dom_backend/dom_backend.js";

const DIM_DESATURATION = 1;
const DIM_BRIGHTNESS = 1;

// A recoloured copy depends only on (source bitmap, recolour), but it used to
// be cached per element, and offer buttons are rebuilt on every state change
// (the 1+2 columns rebuild when the price arrives): the readback-and-loop dim
// and the hue filter ran again each time, ~36 ms per frame on device while an
// offer opened. Shared and weakly keyed, so released images drop their copies.
const dimmed_copies = new WeakMap<object, HTMLCanvasElement>();
const hue_copies = new WeakMap<object, Map<number, HTMLCanvasElement>>();

function dimmedCopy( image: ImageBitmap ): HTMLCanvasElement
{
  const cached = dimmed_copies.get( image );
  if ( cached ) return cached;

  const off_canvas = document.createElement( "canvas" );
  off_canvas.width = image.width;
  off_canvas.height = image.height;
  const off_ctx = off_canvas.getContext( "2d", { willReadFrequently: true } )!;

  off_ctx.drawImage( image, 0, 0 );

  const image_data = off_ctx.getImageData( 0, 0, off_canvas.width, off_canvas.height );
  const data = image_data.data;

  for ( let i = 0; i < data.length; i += 4 )
  {
    if ( data[i + 3] === 0 ) continue;
    const luminance = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    data[i] = Math.round( ( data[i] + ( luminance - data[i] ) * DIM_DESATURATION ) * DIM_BRIGHTNESS );
    data[i + 1] = Math.round( ( data[i + 1] + ( luminance - data[i + 1] ) * DIM_DESATURATION ) * DIM_BRIGHTNESS );
    data[i + 2] = Math.round( ( data[i + 2] + ( luminance - data[i + 2] ) * DIM_DESATURATION ) * DIM_BRIGHTNESS );
  }

  off_ctx.putImageData( image_data, 0, 0 );
  // Placeholder bitmaps (<= 1 px) are replaced once the real image loads.
  if ( image.width > 1 && image.height > 1 )
    dimmed_copies.set( image, off_canvas );
  return off_canvas;
}

function hueRotatedCopy( source: CanvasImageSource & { width: number; height: number }, degrees: number ): HTMLCanvasElement
{
  const cached = hue_copies.get( source )?.get( degrees );
  if ( cached ) return cached;

  const off_canvas = document.createElement( "canvas" );
  off_canvas.width = source.width;
  off_canvas.height = source.height;
  const off_ctx = off_canvas.getContext( "2d" )!;
  off_ctx.filter = `hue-rotate(${degrees}deg)`;
  off_ctx.drawImage( source, 0, 0 );
  off_ctx.filter = "none";

  if ( source.width > 1 && source.height > 1 )
  {
    let by_degrees = hue_copies.get( source );
    if ( !by_degrees )
    {
      by_degrees = new Map();
      hue_copies.set( source, by_degrees );
    }
    by_degrees.set( degrees, off_canvas );
  }
  return off_canvas;
}

export class UIImage extends UIElement
{
  private image: ImageBitmap;
  private img_id: ImgID;

  private corner_radius: number;
  private flip_x = false;
  private dimmed = false;
  private hue_rotation = 0;
  private render_clip?: { x: number; y: number; width: number; height: number };
  // 1 = fully visible, 0 = fully hidden. Crops the image (source AND dest, so
  // the art isn't stretched) to only its bottom fraction — for liquid/meter-
  // style fills that grow up from the bottom instead of stretching.
  private reveal_from_bottom = 1;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    image_id: ImgID,
    options?: TransformOptions,
    is_scalable_on_click = false,
    corner_radius = 0
  )
  {
    super( ctx, x, y, width, height, false, is_scalable_on_click, options );
    this.img_id = image_id;
    this.image = ImagesHolder.getImage( image_id );
    this.corner_radius = corner_radius;
  }
  name():string { return 'UIDrawableImage'; }

  private createRoundedClip( radius: number ): void
  {
    const w = this.rect.getW();
    const h = this.rect.getH();
    const x = -this.rect.getW2();
    const y = -this.rect.getH2();

    this.ctx.beginPath();
    this.ctx.moveTo( x + radius, y );

    this.ctx.lineTo( x + w - radius, y );
    this.ctx.quadraticCurveTo( x + w, y, x + w, y + radius );

    this.ctx.lineTo( x + w, y + h - radius );
    this.ctx.quadraticCurveTo( x + w, y + h, x + w - radius, y + h );

    this.ctx.lineTo( x + radius, y + h );
    this.ctx.quadraticCurveTo( x, y + h, x, y + h - radius );

    this.ctx.lineTo( x, y + radius );
    this.ctx.quadraticCurveTo( x, y, x + radius, y );

    this.ctx.closePath();
    this.ctx.clip();
  }

  public setFlipX( value: boolean ): void
  {
    this.flip_x = value;
    this.setShouldBeRendered();
  }

  public setImageId( image_id: ImgID ): void
  {
    this.img_id = image_id;
    this.image = ImagesHolder.getImage( image_id );
    this.custom_bitmap = false;
    this.dom_bitmap_url = null;
    this.setShouldBeRendered();
  }

  // For swapping in art with different native dimensions (e.g. a box's
  // closed vs. open cap) without recreating the element — repositions and
  // resizes in one go, since a moved top-left would otherwise no longer
  // line up with the new size's intended anchor. Position is driven by
  // Transformable's pixel_pos (not rect.x/y, which the base class always
  // constructs at local 0,0 and only ever uses for width/height).
  public setBounds( x: number, y: number, width: number, height: number ): void
  {
    this.setPixelPos( new Vector2( x, y ), true );
    this.rect.resize( width, height );
    this.setShouldBeRendered();
  }

  private custom_bitmap = false;
  private dom_bitmap_url: string | null = null;

  public setImageBitmap( bitmap: ImageBitmap ): void
  {
    this.image = bitmap;
    this.custom_bitmap = true;
    this.dom_bitmap_url = null;
    if ( this.dom_mode )
      this.makeDomBitmapUrl( bitmap );
    this.setShouldBeRendered();
  }

  /**
   * DOM backend: shows an external picture (a Facebook photo) straight from its URL. The browser
   * loads, decodes and evicts it like any other <img>; a bitmap here would be pinned in memory
   * and copied once more into a blob URL.
   */
  public setImageUrl( url: string ): void
  {
    this.custom_bitmap = true;
    this.dom_bitmap_url = url;
    this.setShouldBeRendered();
  }

  private makeDomBitmapUrl( bitmap: ImageBitmap ): void
  {
    const canvas = document.createElement( "canvas" );
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext( "2d" )?.drawImage( bitmap, 0, 0 );
    canvas.toBlob( blob =>
    {
      if ( !blob || this.image !== bitmap )
        return;
      this.dom_bitmap_url = URL.createObjectURL( blob );
      this.setShouldBeRendered();
    } );
  }

  public setRevealFromBottom( fraction: number ): void
  {
    this.reveal_from_bottom = Math.max( 0, Math.min( 1, fraction ) );
    this.setShouldBeRendered();
  }

  public getImage(): ImageBitmap | null { return this.image; }

  public setDimmed( value: boolean ): void
  {
    if ( this.dimmed === value ) return;
    this.dimmed = value;
    this.setShouldBeRendered();
  }

  /**
   * Recolours the bitmap by rotating its hue, keeping shape, gloss and
   * shadows. Lets one button artwork serve several states (the shop's green
   * price button becomes the purple FREE button) until dedicated art exists.
   */
  public setHueRotation( degrees: number ): void
  {
    const normalized = ( ( degrees % 360 ) + 360 ) % 360;
    if ( this.hue_rotation === normalized ) return;
    this.hue_rotation = normalized;
    this.setShouldBeRendered();
  }

  /**
   * Builds recoloured copies of a loaded image ahead of time, so the first
   * frame of a screen that shows them does not pay for the readback.
   */
  public static prewarmVariants( img_id: ImgID, variants: { dimmed?: boolean; hue?: number }[] ): void
  {
    if ( !ImagesHolder.hasBitmap( img_id ) ) return;
    const image = ImagesHolder.getLoadedImage( img_id );
    if ( !image || image.width <= 1 ) return;
    for ( const variant of variants )
    {
      if ( variant.dimmed ) dimmedCopy( image );
      if ( variant.hue ) hueRotatedCopy( image, ( ( variant.hue % 360 ) + 360 ) % 360 );
    }
  }

  /** Clips rendering in image-local coordinates measured from its top-left. */
  public setRenderClip( x: number, y: number, width: number, height: number ): void
  {
    this.render_clip = { x, y, width, height };
    this.setShouldBeRendered();
  }

  public renderRaw(): void
  {
    if ( this.image.width === 0 )
      return;

    this.ctx.drawImage( this.image, -this.rect.getW2(), -this.rect.getH2(), this.rect.getW(), this.rect.getH() );
  }

  private dom_last_key: string = "";
  private dom_img: HTMLImageElement | null = null;

  private decodedBytes(): number
  {
    const own = this.image ? this.image.width * this.image.height * 4 : 0;
    return this.custom_bitmap ? own : Math.max( own, ImagesHolder.getDecodedBytes( this.img_id ) );
  }

  protected override syncDomContent( node: HTMLElement ): boolean
  {
    const url = this.custom_bitmap ? this.dom_bitmap_url : ImagesHolder.getUrl( this.img_id );
    if ( !url )
      return false;

    const filters: string[] = [];
    if ( this.dimmed ) filters.push( "grayscale(1)" );
    else if ( this.darkness_factor > 0 ) filters.push( `brightness(${1 - this.darkness_factor})` );
    if ( this.hue_rotation !== 0 && !this.dimmed ) filters.push( `hue-rotate(${this.hue_rotation}deg)` );
    const clip = this.render_clip
      ? `inset(${this.render_clip.y}px ${this.rect.getW() - this.render_clip.x - this.render_clip.width}px ${this.rect.getH() - this.render_clip.y - this.render_clip.height}px ${this.render_clip.x}px)`
      : this.reveal_from_bottom < 1
        ? `inset(${( 1 - this.reveal_from_bottom ) * 100}% 0 0 0)`
        : "";
    const radius = this.corner_radius > 0 ? `${this.corner_radius}px` : "";
    const sync_decode = this.decodedBytes() >= DOM_SYNC_DECODE_MIN_BYTES;
    const key = `${url}|${filters.join( " " )}|${clip}|${radius}|${sync_decode}`;
    if ( key === this.dom_last_key )
      return true;
    this.dom_last_key = key;

    const s = node.style;
    if ( sync_decode )
    {
      this.dom_img = mountDomSyncImage( node, this.dom_img, url );
      s.backgroundImage = "";
    }
    else
    {
      this.dom_img?.remove();
      this.dom_img = null;
      s.backgroundImage = `url("${url}")`;
      s.backgroundSize = "100% 100%";
      s.backgroundRepeat = "no-repeat";
    }
    s.filter = filters.join( " " );
    s.clipPath = clip;
    s.borderRadius = radius;
    s.overflow = radius ? "hidden" : "";
    return true;
  }

  protected override domStaticTransform(): string
  {
    const base = super.domStaticTransform();
    if ( !this.flip_x ) return base;
    return base ? `${base} scaleX(-1)` : "scaleX(-1)";
  }

  render(): void
  {
  if ( !this.shouldBeRendered() )
    return;

  if ( this.image.width <= 1 && !this.dom_mode )
  {
    const fresh = ImagesHolder.getLoadedImage( this.img_id );
    if ( fresh && fresh !== this.image )
    {
      this.image = fresh;
    }
    else if ( this.image.width === 0 )
    {
      void ImagesHolder.ensure( this.img_id ).catch( () => {} );
      return;
    }
  }

  this.startRender();

  if ( this.isDomNative() )
  {
    this.finishRender();
    return;
  }

  const w = this.rect.getW();
  const h = this.rect.getH();
  const w2 = this.rect.getW2();
  const h2 = this.rect.getH2();

  this.applyTransform();

  if ( this.flip_x )
    this.ctx.scale( -1, 1 );

  if ( this.render_clip )
  {
    this.ctx.beginPath();
    this.ctx.rect(
      -w2 + this.render_clip.x,
      -h2 + this.render_clip.y,
      this.render_clip.width,
      this.render_clip.height
    );
    this.ctx.clip();
  }

    if ( this.corner_radius > 0 )
    {
      this.createRoundedClip( this.corner_radius );
    }

  const base_source = this.dimmed
    ? dimmedCopy( this.image )
    : this.darkness_factor === 0 ? this.image : this.createDarkenedImage( this.image );
  const source = this.hue_rotation !== 0 && !this.dimmed ? hueRotatedCopy( base_source, this.hue_rotation ) : base_source;

  if ( this.reveal_from_bottom >= 1 )
  {
    this.ctx.drawImage( source, -w2, -h2, w, h );
  }
  else if ( this.reveal_from_bottom > 0 )
  {
    const sh = source.height * this.reveal_from_bottom;
    const sy = source.height - sh;
    const dh = h * this.reveal_from_bottom;
    const dy = h2 - dh;
    this.ctx.drawImage( source, 0, sy, source.width, sh, -w2, dy, w, dh );
  }

  this.finishRender();
}

  public getRectX(): number { return this.rect.getX(); }
  public getRectY(): number { return this.rect.getY(); }
  public getRectW(): number { return this.rect.getW(); }
  public getRectH(): number { return this.rect.getH(); }
}
